/**
 * BiblicalReferencePanel - Panel de referencia bíblica mejorado
 * Muestra todas las referencias bíblicas de las perspectivas y permite seleccionar
 */

import React, { useState, useEffect } from 'react';
import { Book, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import BibleReaderCompact from '../Bible/BibleReaderCompact';

const BiblicalReferencePanel = ({ mitzvah }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedReference, setSelectedReference] = useState(null);

  // Obtener todas las referencias bíblicas de todas las perspectivas
  const getAllBiblicalReferences = () => {
    const references = [];
    
    // Prioridad: Rambam > Ramban > Chinuch > SEMAG > BAHAG > Hebrew Roots
    const perspectivesList = [
      { key: 'rambam', name: 'Rambam (Maimónides)', data: mitzvah.rambamPerspectives, color: 'blue' },
      { key: 'ramban', name: 'Ramban (Najmánides)', data: mitzvah.rambanPerspectives, color: 'purple' },
      { key: 'chinuch', name: 'Chinuch', data: mitzvah.chinuchPerspectives, color: 'indigo' },
      { key: 'semag', name: 'SEMAG', data: mitzvah.semagPerspectives, color: 'pink' },
      { key: 'bahag', name: 'BAHAG', data: mitzvah.bahagPerspectives, color: 'orange' },
      { key: 'hebrewRoots', name: 'Raíces Hebreas', data: mitzvah.hebrewRootsPerspectives, color: 'green' }
    ];

    perspectivesList.forEach(perspective => {
      if (perspective.data && perspective.data.length > 0) {
        perspective.data.forEach(item => {
          if (item.sourceVerse) {
            references.push({
              perspective: perspective.name,
              perspectiveKey: perspective.key,
              color: perspective.color,
              sourceVerse: item.sourceVerse
            });
          }
          // Agregar sourceVerse2 si existe (para Hebrew Roots)
          if (item.sourceVerse2) {
            references.push({
              perspective: perspective.name + ' (adicional)',
              perspectiveKey: perspective.key,
              color: perspective.color,
              sourceVerse: item.sourceVerse2
            });
          }
        });
      }
    });

    return references;
  };

  const allReferences = getAllBiblicalReferences();

  // Seleccionar la primera referencia (Rambam por defecto) cuando se monta el componente
  useEffect(() => {
    if (allReferences.length > 0 && !selectedReference) {
      setSelectedReference(allReferences[0]);
    }
  }, [allReferences.length]);

  if (allReferences.length === 0) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 mb-6">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-800">
            No hay referencias bíblicas disponibles para este mandamiento
          </p>
        </div>
      </div>
    );
  }

  const currentVerse = selectedReference?.sourceVerse;
  const colorClasses = {
    blue: { bg: 'from-blue-50 to-blue-100', border: 'border-blue-300', button: 'bg-blue-600 hover:bg-blue-700', buttonActive: 'bg-blue-700' },
    purple: { bg: 'from-purple-50 to-purple-100', border: 'border-purple-300', button: 'bg-purple-600 hover:bg-purple-700', buttonActive: 'bg-purple-700' },
    indigo: { bg: 'from-indigo-50 to-indigo-100', border: 'border-indigo-300', button: 'bg-indigo-600 hover:bg-indigo-700', buttonActive: 'bg-indigo-700' },
    pink: { bg: 'from-pink-50 to-pink-100', border: 'border-pink-300', button: 'bg-pink-600 hover:bg-pink-700', buttonActive: 'bg-pink-700' },
    orange: { bg: 'from-orange-50 to-orange-100', border: 'border-orange-300', button: 'bg-orange-600 hover:bg-orange-700', buttonActive: 'bg-orange-700' },
    green: { bg: 'from-green-50 to-green-100', border: 'border-green-300', button: 'bg-green-600 hover:bg-green-700', buttonActive: 'bg-green-700' }
  };

  const currentColor = colorClasses[selectedReference?.color] || colorClasses.blue;

  return (
    <div className={`bg-white rounded-lg shadow-md border-2 ${currentColor.border} overflow-hidden mb-6`}>
      {/* Header - Siempre visible */}
      <div className={`bg-gradient-to-r ${currentColor.bg} px-6 py-4 border-b-2 ${currentColor.border}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${currentColor.button} flex items-center justify-center`}>
              <Book className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Referencia Bíblica Principal
              </p>
              <h3 className="text-lg font-bold text-gray-900">
                {currentVerse?.book?.bookNameEs || currentVerse?.book?.bookName}{' '}
                {currentVerse?.chapter}:{currentVerse?.verse}
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                Según {selectedReference?.perspective}
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setExpanded(!expanded)}
            className={`flex items-center gap-2 px-4 py-2 ${currentColor.button} text-white rounded-lg transition-colors font-medium`}
          >
            {expanded ? (
              <>
                <ChevronUp className="w-5 h-5" />
                Ocultar
              </>
            ) : (
              <>
                <ChevronDown className="w-5 h-5" />
                Ver Texto Completo
              </>
            )}
          </button>
        </div>

        {/* Selector de referencias (si hay más de una) */}
        {allReferences.length > 1 && (
          <div className="mt-3">
            <p className="text-xs font-semibold text-gray-700 mb-2">
              Seleccionar referencia de otra perspectiva:
            </p>
            <div className="flex flex-wrap gap-2">
              {allReferences.map((ref, index) => {
                const isSelected = selectedReference === ref;
                const refColor = colorClasses[ref.color];
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedReference(ref)}
                    className={`
                      px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-all
                      ${isSelected ? refColor.buttonActive : refColor.button}
                      ${isSelected ? 'ring-2 ring-white ring-offset-2' : ''}
                    `}
                  >
                    {ref.perspective}: {ref.sourceVerse.book?.bookNameEs} {ref.sourceVerse.chapter}:{ref.sourceVerse.verse}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Panel de lectura expandible - Integrado con BibleReaderCompact */}
      {expanded && selectedReference && (
        <div className="px-6 py-6 bg-white">
          <BibleReaderCompact
            versionId={1} // RVR1960 por defecto
            bookName={selectedReference.sourceVerse.book?.bookNameEs || selectedReference.sourceVerse.book?.bookName}
            chapter={selectedReference.sourceVerse.chapter}
            focusVerses={[selectedReference.sourceVerse.verse]}
            maxHeight="400px"
            showStrongs={true}
          />
          
          {/* Información adicional */}
          <div className="mt-4 text-xs text-gray-600 italic bg-gray-50 rounded-lg p-3">
            <p>
              💡 Este versículo es la referencia bíblica según {selectedReference?.perspective}.
            </p>
            {allReferences.length > 1 && (
              <p className="mt-1">
                Hay {allReferences.length} referencias bíblicas disponibles de diferentes perspectivas.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BiblicalReferencePanel;
